import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatPhone } from './formatters';

/**
 * Render a single customer agreement PDF that matches the Proxemics
 * "Proposal for Liaison Coverage" print template (see
 * Proxemics-ProposalTemplate.pdf). Structure, top → bottom:
 *
 *   • Proxemics logo (top-right)
 *   • Long-form date (top-left)
 *   • Two-column address block: Proxemics sender (left) / customer (right)
 *     — no section labels, each column leads with a bolded contact name
 *   • Horizontal divider line
 *   • Large title: "Proposal for Liaison Coverage"
 *   • Dynamic intro paragraph composed from the customer + plant +
 *     requestor fields, e.g. "This proposal outlines on-site liaison
 *     representation for ABC Climate Control at GM Factory Zero,
 *     requested by Richard Lee."
 *   • Static Intro paragraph (defaults to the template's
 *     "Proxemics Consulting provides embedded liaison support…" sentence)
 *   • Orange section heading "On-Site Liaison Support" + "Our liaison will:"
 *     intro + bullet list
 *   • Orange section heading "Coverage & Rate" + 3-column table
 *   • Closing paragraph about out-of-scope coverage + hourly rate
 *   • Orange section heading "Terms" + bullet list
 *   • Footer on every page: "Proxemics Consulting / Service Proposal"
 *     (left) and the page number (right)
 *
 * The acceptance / signature block was intentionally removed so the
 * generated PDF matches the printed template exactly — Jillian signs
 * these separately.
 */

// Kept in sync with --brand-primary / --brand-ink / --brand-muted /
// --brand-border in theme.css.
const BRAND = [232, 119, 34]; // #E87722 — logo orange
const INK = [30, 41, 59]; // #1E293B — slate-800, matches template body
const MUTED = [100, 116, 139]; // #64748b
const DIVIDER = [209, 213, 219]; // #d1d5db — slightly darker for visibility

const MARGIN_X = 56; // ~0.78" — matches template's outer margin
const PAGE_TOP = 48;
const FOOTER_RESERVE = 56; // bottom-page reserve for the running footer

// Proxemics HQ — always renders on the "Prepared by" side of the letter
// regardless of which Director signed the proposal. If Proxemics ever
// opens a second office, promote this to configuration.
const PROXEMICS_SENDER = {
  company: 'Proxemics Consulting',
  street: '12451 Flora Drive',
  cityStateZip: 'Brighton, MI 48114',
};

const LOGO_URL = '/Proxemics-Logo.png';

// Cache the logo load between PDF builds so repeated "Save as PDF"
// clicks don't refetch + redraw the PNG each time. The promise is
// reset on failure so a transient network hiccup doesn't permanently
// block the logo on this tab.
let logoPromise = null;
function loadLogo() {
  if (logoPromise) return logoPromise;
  logoPromise = new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      resolve({
        dataUrl: canvas.toDataURL('image/png'),
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    };
    img.onerror = (err) => {
      logoPromise = null;
      reject(err);
    };
    img.src = LOGO_URL;
  });
  return logoPromise;
}

function dashIfBlank(v) {
  const s = (v ?? '').toString().trim();
  return s || '—';
}

function formatDateLong(iso) {
  if (!iso) return '';
  // iso may be "2026-04-18" or a full ISO timestamp. Parse as a plain date
  // so timezone offsets can't shift the displayed day.
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return iso;
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function formatCurrency(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// Layout engine with auto-pagination — mirrors the one in plantPdf.js so
// sections don't have to think about page breaks.
function makeLayout(doc) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const usableWidth = pageWidth - MARGIN_X * 2;
  const state = { y: PAGE_TOP };

  const ensureRoom = (needed) => {
    if (state.y + needed > pageHeight - FOOTER_RESERVE) {
      doc.addPage();
      state.y = PAGE_TOP;
    }
  };

  return {
    pageWidth,
    pageHeight,
    usableWidth,
    get y() {
      return state.y;
    },
    advance(n) {
      state.y += n;
    },
    moveTo(n) {
      state.y = n;
    },
    ensureRoom,
  };
}

// ---------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------

/**
 * Top of page 1: logo in the top-right corner, long-form date in the
 * top-left, two-column address block underneath, and a thin horizontal
 * divider line to close the block out.
 *
 * Template reference: top third of Proxemics-ProposalTemplate.pdf.
 */
function drawLetterhead(doc, layout, agreement, logo) {
  const topY = PAGE_TOP;

  // Logo — top-right, sized so its height matches the height of the
  // six-line address block below (keeps the eye level between the two).
  if (logo) {
    const logoHeight = 56;
    const logoWidth = (logo.width / logo.height) * logoHeight;
    doc.addImage(
      logo.dataUrl,
      'PNG',
      layout.pageWidth - MARGIN_X - logoWidth,
      topY,
      logoWidth,
      logoHeight,
    );
  }

  // Date — top-left, same baseline as where the logo starts.
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(...INK);
  const dateStr = formatDateLong(
    agreement.ProposalDate || new Date().toISOString().slice(0, 10),
  );
  doc.text(dateStr, MARGIN_X, topY + 14);

  // Start the address block below whichever took more vertical room —
  // the date line (small) or the logo (larger). In practice the logo
  // wins, so start the addresses ~logo-height below the page top.
  const addressTop = topY + (logo ? 80 : 40);

  // Two-column address block. No "PREPARED BY / FOR" labels — the
  // template doesn't use them. Each column leads with a bolded name
  // (Chris Maxwell / Richard Lee in the template) followed by plain-
  // text company, street, city/state/zip, phone lines.
  const colGap = 32;
  const colWidth = (layout.usableWidth - colGap) / 2;
  const leftX = MARGIN_X;
  const rightX = MARGIN_X + colWidth + colGap;

  const customerCityStateZip = [
    [agreement.CustomerCity, agreement.CustomerState]
      .filter(Boolean)
      .join(', '),
    agreement.CustomerZip,
  ]
    .filter(Boolean)
    .join(' ')
    .trim();

  // Each column is split into [headingLine, ...bodyLines] so we can
  // bold the first line. `null` entries collapse (so a missing phone
  // doesn't leave a blank line).
  const leftCol = {
    heading: agreement.PreparedByName || PROXEMICS_SENDER.company,
    body: [
      // Only include the company as a second line if the first line is
      // a person's name (otherwise we'd print the company twice).
      agreement.PreparedByName ? PROXEMICS_SENDER.company : null,
      PROXEMICS_SENDER.street,
      PROXEMICS_SENDER.cityStateZip,
      formatPhone(agreement.PreparedByPhone) || null,
    ].filter(Boolean),
  };

  const rightCol = {
    heading: agreement.CustomerContactName || agreement.CustomerCompany || '',
    body: [
      agreement.CustomerContactName ? agreement.CustomerCompany : null,
      agreement.CustomerStreet,
      customerCityStateZip,
      formatPhone(agreement.CustomerPhone) || null,
    ].filter(Boolean),
  };

  const drawCol = (col, x) => {
    let y = addressTop;
    if (col.heading) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(...INK);
      doc.text(col.heading, x, y, { maxWidth: colWidth });
      y += 14;
    }
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(...INK);
    col.body.forEach((line) => {
      doc.text(line, x, y, { maxWidth: colWidth });
      y += 14;
    });
    return y;
  };

  const leftEnd = drawCol(leftCol, leftX);
  const rightEnd = drawCol(rightCol, rightX);
  const addressEnd = Math.max(leftEnd, rightEnd);

  // Horizontal divider line separating the letterhead from the title.
  const dividerY = addressEnd + 14;
  doc.setDrawColor(...DIVIDER);
  doc.setLineWidth(0.75);
  doc.line(MARGIN_X, dividerY, layout.pageWidth - MARGIN_X, dividerY);

  layout.moveTo(dividerY + 28);
}

/**
 * Large proposal title — dark slate, bold, ~28pt. Matches the template's
 * "Proposal for Liaison Coverage" headline.
 */
function drawTitle(doc, layout, agreement) {
  const title = agreement.ProposalTitle || 'Proposal for Liaison Coverage';
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(26);
  doc.setTextColor(...INK);
  doc.text(title, MARGIN_X, layout.y);
  layout.advance(28);
}

/**
 * Turn a list of plant names into a readable English clause:
 *   []               → ''
 *   ['A']            → 'A'
 *   ['A', 'B']       → 'A and B'
 *   ['A', 'B', 'C']  → 'A, B, and C'
 * Duplicates and blanks are dropped so a repeat-plant row doesn't echo.
 */
function formatPlantList(plants) {
  const seen = new Set();
  const clean = [];
  for (const p of plants || []) {
    const v = (p || '').trim();
    if (!v || seen.has(v)) continue;
    seen.add(v);
    clean.push(v);
  }
  if (clean.length === 0) return '';
  if (clean.length === 1) return clean[0];
  if (clean.length === 2) return `${clean[0]} and ${clean[1]}`;
  return `${clean.slice(0, -1).join(', ')}, and ${clean[clean.length - 1]}`;
}

/**
 * Compose the dynamic first intro paragraph from the form fields:
 *   "This proposal outlines on-site liaison representation for
 *    <Company> at <Plant>[, <Plant>, and <Plant>]."
 *
 * The plant list is derived from the Coverage & Rate rows on the form,
 * so the Director doesn't have to retype plant names. Each clause is
 * only included if the backing data is populated — so a proposal without
 * a customer company reads naturally instead of a dangling fragment.
 */
function buildDynamicIntro(agreement) {
  const company = (agreement.CustomerCompany || '').trim();
  const plantList = formatPlantList(
    (agreement.coverageRows || []).map((r) => r && r.plant),
  );

  if (!company && !plantList) return '';

  const forClause = company ? ` for ${company}` : '';
  const atClause = plantList ? ` at ${plantList}` : '';

  return `This proposal outlines on-site liaison representation${forClause}${atClause}.`;
}

function drawParagraph(doc, layout, text, { extraGap = 8 } = {}) {
  if (!text) return;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(...INK);
  const lines = doc.splitTextToSize(text, layout.usableWidth);
  layout.ensureRoom(lines.length * 14 + extraGap);
  doc.text(lines, MARGIN_X, layout.y);
  layout.advance(lines.length * 14 + extraGap);
}

/**
 * Orange Title-Case section heading with a small amount of breathing
 * room above and below. No underline — the template doesn't use one.
 */
function sectionHeading(doc, layout, title) {
  layout.ensureRoom(28);
  layout.advance(6);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...BRAND);
  doc.text(title, MARGIN_X, layout.y);
  layout.advance(16);
}

function drawBulletList(doc, layout, bullets) {
  if (!bullets || bullets.length === 0) return;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(...INK);
  const bulletX = MARGIN_X + 4;
  const textX = MARGIN_X + 16;
  const wrapWidth = layout.usableWidth - 16;
  bullets.forEach((b) => {
    const lines = doc.splitTextToSize(b, wrapWidth);
    const needed = lines.length * 14 + 2;
    layout.ensureRoom(needed);
    doc.text('•', bulletX, layout.y);
    doc.text(lines, textX, layout.y);
    layout.advance(needed);
  });
  layout.advance(6);
}

/**
 * "On-Site Liaison Support" section — orange heading, plain "Our liaison
 * will:" lead-in, then a bullet list. The lead-in isn't stored with the
 * bullets because every proposal uses the same one, and Directors
 * shouldn't have to type it in.
 */
function drawSupportSection(doc, layout, bullets) {
  if (!bullets || bullets.length === 0) return;
  sectionHeading(doc, layout, 'On-Site Liaison Support');

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(...INK);
  layout.ensureRoom(18);
  doc.text('Our liaison will:', MARGIN_X, layout.y);
  layout.advance(16);

  drawBulletList(doc, layout, bullets);
}

function drawCoverageTable(doc, layout, rows) {
  if (!rows || rows.length === 0) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(10);
    doc.setTextColor(...MUTED);
    layout.ensureRoom(16);
    doc.text('Coverage rows to be added.', MARGIN_X, layout.y);
    layout.advance(16);
    return;
  }
  autoTable(doc, {
    head: [['Plant', 'Hourly Rate (USD)', 'Coverage Per Production Day']],
    body: rows.map((r) => [
      dashIfBlank(r.plant),
      r.hourlyRateUsd != null && r.hourlyRateUsd !== ''
        ? formatCurrency(r.hourlyRateUsd)
        : '—',
      dashIfBlank(r.coverage),
    ]),
    startY: layout.y,
    margin: { left: MARGIN_X, right: MARGIN_X },
    styles: {
      font: 'helvetica',
      fontSize: 11,
      textColor: INK,
      cellPadding: 8,
      lineColor: DIVIDER,
      lineWidth: 0.5,
      valign: 'middle',
    },
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: MUTED,
      fontStyle: 'normal',
      fontSize: 10,
      lineColor: DIVIDER,
      lineWidth: 0.5,
    },
    // Body rows bolded to match the template's emphasis on the coverage
    // row content ("GM Factory Zero | $35.00 | 2 Hours" is rendered in
    // bold slate in the printed reference).
    bodyStyles: { fontStyle: 'bold' },
    theme: 'grid',
  });
  // Extra breathing room after the table so the "Additional support…"
  // paragraph doesn't sit right up against the grid.
  layout.moveTo(doc.lastAutoTable.finalY + 24);
}

/**
 * Post-table paragraph explaining the hourly rate applies to any extra
 * coverage outside the scheduled block. Mirrors the template verbatim,
 * inserting the customer company name when present.
 */
function drawAdditionalSupportParagraph(doc, layout, agreement) {
  const company = (agreement.CustomerCompany || '').trim() || 'the customer';
  const text =
    'Additional support outside scheduled coverage or outside normal ' +
    'production hours will be invoiced at the same hourly rate with ' +
    `prior approval by ${company}.`;
  drawParagraph(doc, layout, text, { extraGap: 10 });
}

/**
 * Build the "Terms" bullets, wording tuned to match the printed template.
 * Two bullets are always included on every proposal — expenses must be
 * pre-approved, and Proxemics does not require contracts — so they live
 * here instead of on the form. The "Additional Terms" textarea is
 * appended afterward as a separate paragraph.
 */
function buildTermsBullets(agreement) {
  const company = (agreement.CustomerCompany || '').trim() || 'the customer';
  const terms = [];
  if (agreement.PaymentTerms) {
    terms.push(`Payment terms: ${agreement.PaymentTerms}`);
  }
  terms.push(`Expenses must be pre-approved by ${company}.`);
  terms.push('Proxemics does not require contracts.');
  const days = Number(agreement.CancellationNoticeDays);
  if (Number.isFinite(days) && days > 0) {
    terms.push(
      `${days}-day notice is requested for cancellation of services.`,
    );
  }
  return terms;
}

function drawTermsSection(doc, layout, agreement) {
  const bullets = buildTermsBullets(agreement);
  if (bullets.length === 0 && !agreement.AdditionalTerms) return;
  sectionHeading(doc, layout, 'Terms');
  if (bullets.length) drawBulletList(doc, layout, bullets);
  if (agreement.AdditionalTerms) {
    drawParagraph(doc, layout, agreement.AdditionalTerms);
  }
}

/**
 * Running footer on every page — "Proxemics Consulting / Service Proposal"
 * on the left and the page number on the right. Drawn after the content
 * is laid out so the total page count is known.
 */
function drawFooter(doc) {
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...MUTED);
    const footerY = doc.internal.pageSize.getHeight() - 28;
    doc.text(
      'Proxemics Consulting / Service Proposal',
      MARGIN_X,
      footerY,
    );
    doc.text(
      String(i),
      doc.internal.pageSize.getWidth() - MARGIN_X,
      footerY,
      { align: 'right' },
    );
  }
}

/**
 * Build the proposal PDF without saving it. Exposed separately so callers
 * that want to preview / blob-stream the doc (e.g. future in-app preview)
 * don't have to re-implement the layout.
 */
export async function buildAgreementPdf(agreement) {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const layout = makeLayout(doc);

  // Best-effort logo fetch; a failure falls back to text-only header.
  let logo = null;
  try {
    logo = await loadLogo();
  } catch (err) {
    console.warn('[proposalPdf] logo load failed, continuing without it', err);
  }

  drawLetterhead(doc, layout, agreement, logo);
  drawTitle(doc, layout, agreement);

  // Intro: dynamic first paragraph ("This proposal outlines…") followed
  // by the static Intro from the form (defaults to "Proxemics Consulting
  // provides embedded liaison support…"). Directors can edit the static
  // paragraph on the form; the dynamic one is always derived from
  // customer / plant / requestor fields.
  const dynamicIntro = buildDynamicIntro(agreement);
  if (dynamicIntro) drawParagraph(doc, layout, dynamicIntro, { extraGap: 10 });
  if (agreement.Intro) drawParagraph(doc, layout, agreement.Intro, { extraGap: 12 });

  // On-Site Liaison Support: heading + "Our liaison will:" + bullets.
  drawSupportSection(doc, layout, agreement.supportBullets || []);

  // Coverage & Rate table + the "additional support" clause underneath.
  sectionHeading(doc, layout, 'Coverage & Rate');
  drawCoverageTable(doc, layout, agreement.coverageRows || []);
  drawAdditionalSupportParagraph(doc, layout, agreement);

  // Terms bullets + optional additional terms paragraph.
  drawTermsSection(doc, layout, agreement);

  drawFooter(doc);

  return doc;
}

/**
 * Generate and download the proposal PDF. Filename pattern:
 *   ProxemicsConsulting-Proposal-{CustomerSlug}-{MM.DD.YYYY}.pdf
 *   (e.g. ProxemicsConsulting-Proposal-Test-04.19.2026.pdf)
 * US-style date chosen by Jillian — reads more naturally at-a-glance
 * than ISO. The date component still makes it easy to tell multiple
 * revisions of the same customer's proposal apart.
 */
export async function saveAgreementPdf(agreement) {
  const doc = await buildAgreementPdf(agreement);
  const slug =
    (agreement.CustomerCompany || 'Proposal')
      .replace(/[^a-z0-9]+/gi, '-')
      .replace(/(^-|-$)/g, '') || 'Proposal';
  // ProposalDate is stored as an ISO string (YYYY-MM-DD). Reformat to
  // MM.DD.YYYY for the filename; fall back to empty if the date is
  // missing or malformed so we don't produce "NaN.NaN.NaN".
  const iso = (agreement.ProposalDate || '').slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  const date = m ? `${m[2]}.${m[3]}.${m[1]}` : '';
  const identifier = [slug, date].filter(Boolean).join('-');
  doc.save(`ProxemicsConsulting-Proposal-${identifier}.pdf`);
}
